// Auth helper module: centralized login/profile functions
// Exported functions:
// - getUserProfile() -> returns profile object
// - loginApi(apiCallObj, opts) -> performs login, stores token, returns token
// - authenticateCurUser(opts) -> handles page-level auth flow (redirects/dom updates) and returns {loggedIn, profile}
import { homePageImages } from '../img_sound_imports.js';
import { apiRoutes } from '../apiRoutes.js';
import {
  authenticatedFetch,
  ensureFreshSession,
  redirectToPortalLogin,
  setAccessToken,
} from './authSession.js';
import { loginRedirectReturnTarget } from '../orgContext.js';

// Fetches a presigned S3 URL for the profile photo object.
// profilePhoto from API is an object: { resourceId, availability, objects: [{ objectId, ... }] }
async function fetchProfilePhotoUrl(profilePhoto) {
  if (!profilePhoto) return null;
  // Old API: plain string URL — use directly
  if (typeof profilePhoto === 'string') return profilePhoto;
  // New API: object — must call resource-service to get presigned URL
  if (profilePhoto.availability !== 'AVAILABLE' || !profilePhoto.objects?.length) return null;

  const { resourceId } = profilePhoto;
  const { objectId } = profilePhoto.objects[0];

  try {
    const res = await authenticatedFetch(apiRoutes.resourceAccess(resourceId, objectId), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.accessUrl || null;
  } catch (err) {
    console.warn('fetchProfilePhotoUrl: failed to get presigned URL', err);
    return null;
  }
}

export async function getUserProfile() {
  try {
    const res = await authenticatedFetch(apiRoutes.profile(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Profile request failed: ${res.status} ${text}`);
    }
    const content = await res.json();

    const profilePhoto = await fetchProfilePhotoUrl(content.profilePhoto);

    return {
      firstName: content.firstName,

      profilePhoto,
      raw: content,
    };
  } catch (err) {
    console.error('getUserProfile error:', err);
    throw err;
  }
}

export async function loginApi(apiCallObj, opts = {}) {
  const { user, pass, urlRedirect = 'home.html', showMsg, currLang, curPage, loginMode } = opts;
  if (!apiCallObj) throw new Error('loginApi: missing apiCallObj');
  try {
    const response = await fetch(apiRoutes.login(), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        countryCode: '+91',
        loginWith: 'PASSWORD',
        username: user,
        password: pass,
      }),
    });
    if (!response.ok) {
      const text = await response.text();
      // Show error message if callback provided
      if (showMsg && currLang) showMsg(currLang['Game.ErrorText']);
      throw new Error(`Login failed: ${response.status} ${text}`);
    }
    const content = await response.json();
    if (content.token) {
      setAccessToken(content.token);
      if (showMsg && currLang) showMsg(currLang['SucessMsg.Login']);
      // redirect by caller or directly
      window.location.href = urlRedirect;
      return content.token;
    } else {
      if (showMsg && currLang) showMsg(currLang['Game.ErrorText']);
      window.location.href = `${loginMode}.html?${curPage}`;
      return null;
    }
  } catch (err) {
    console.error('loginApi error:', err);
    if (showMsg && currLang) showMsg(currLang['Game.ErrorText']);
    throw err;
  }
}

export async function authenticateCurUser(opts = {}) {
  const {
    apiCallObj,
    curPage = '',
    loginMode = 'login_redirect',
    showMsg,
    currLang,
    requestFromDatabase,
  } = opts;

  try {
    let formContainer = document.querySelector('.form-container');
    let loggedIn = false;
    if (apiCallObj && apiCallObj.userToken) loggedIn = true;
    if (!loggedIn && !curPage.includes(loginMode)) {
      loggedIn = await ensureFreshSession();
    }

    const onLoginPage = curPage.includes(loginMode);

    if (!onLoginPage) {
      if (!loggedIn) {
        redirectToPortalLogin();
        return { loggedIn: false };
      } else {
        let logoutBtn = document.querySelector('.logout-btn');
        if (logoutBtn) logoutBtn.style.display = 'flex';
        // fetch profile (best-effort)
        try {
          const profile = await getUserProfile();
          // update some DOM (kept as before)
          const displayUser = document.querySelector('.username-display');

          // Resolve gender-correct avatar (fallback to boy if gender unknown)
          const isMale = profile.raw.gender !== 'FEMALE';
          const avatarSrc = isMale ? homePageImages.profileBoy : homePageImages.profileGirl;
          const displaySrc = profile.profilePhoto || avatarSrc;

          // ── profile-btn (top-right circle) ──────────────────────────────
          const profileOption = document.querySelector('.profile-btn');
          if (profileOption) {
            profileOption.innerHTML = `<img src="${displaySrc}">`;
            const showNav = new URLSearchParams(window.location.search).get('shownav') !== 'false';

            profileOption.style.display = showNav ? 'block' : 'none';
          }

          // ── dropdown profile image ───────────────────────────────────────
          const profileHeadingImg = document.querySelector('.user-profile-heading-img');
          if (profileHeadingImg) {
            profileHeadingImg.src = displaySrc;
          }
          if (displayUser) {
            displayUser.textContent = `${currLang['Home.Greeting']}, ` + profile.firstName;
            displayUser.style.display = 'block';
          }

          // Mirror the now-loaded avatar onto the header's small trigger
          // button (appHeader.js exposes this only if mountAppHeader() ran
          // on this page — safe no-op otherwise).
          window.__syncHeaderAvatar?.();
        } catch (err) {
          console.warn("authenticateCurUser: couldn't fetch profile:", err);
          // Remove loading state and fall back to default boy avatar
          const profileOption = document.querySelector('.profile-btn');
          if (profileOption) profileOption.innerHTML = `<img src="${homePageImages.profileBoy}">`;
          const profileHeadingImg = document.querySelector('.user-profile-heading-img');
          if (profileHeadingImg) {
            profileHeadingImg.src = homePageImages.profileBoy;
          }

          // Same sync call on the fallback path — header trigger should
          // still mirror the default boy avatar, not stay stuck on the icon.
          window.__syncHeaderAvatar?.();
        }
        const sharedId = new URLSearchParams(window.location.search).get('shared');
        if (sharedId && requestFromDatabase) {
          requestFromDatabase(sharedId);
        }
        return { loggedIn: true };
      }
    } else {
      // on login page
      if (loggedIn) {
        if (curPage.includes('login_redirect')) {
          if (formContainer) formContainer.style.display = 'none';
        } else {
          const bd = document.querySelector('#blocklyDiv');
          const bb = document.querySelector('#blocklyBackground');
          const ib = document.querySelector('.info-button');
          if (bd) bd.style.display = 'none';
          if (bb) bb.style.display = 'none';
          if (ib) ib.style.display = 'none';
        }
        if (showMsg) showMsg(`You are already logged in `);
        let formEl = document.querySelector('.colm-logo');
        const urlToRedirect = loginRedirectReturnTarget();
        if (formEl && currLang) {
          formEl.insertAdjacentHTML(
            'beforeend',
            `<a href=\"./${urlToRedirect}\"><button class=\"button redirect-btn\" >${currLang['GoBackButton.Text']}</button></a>`
          );
        }
        return { loggedIn: true };
      }
    }
  } catch (err) {
    console.error('authenticateCurUser error:', err);
    if (showMsg && currLang) showMsg(currLang['Game.ErrorText']);
    throw err;
  }
}
